import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';

import { DividerWithText } from '../../../../../components/DividerWithText';
import { LoadingButton } from '../../../../../components/LoadingButton';
import { Modal } from '../../../../../components/Modal';
import { SearchInput } from '../../../../../components/searchInput/SearchInput';
import { SlidingMenu } from '../../../../../components/SlidingMenu';
import { Stop } from '../../../../../domain/stop/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { Text } from '../../../../../react-components/Text';
import { RouteActionType } from '../../context/actions';
import { useRouteContext } from '../../context/context';
import { AddStopsList } from '../AddStopsList';
import { LocationPill } from '../LocationPill';

interface AddStopsSlidingMenuProps {
  open: boolean;
  addedStops: Stop[];
  onRemoveStop: (stop: Stop) => void;
  onAddLocation: (location: Location) => void;
  onClose: () => void;
  onExit: () => void;
  slideStyles?: React.CSSProperties;
}

export const AddStopsSlidingMenu = ({
  open,
  addedStops,
  onRemoveStop,
  onAddLocation,
  onClose,
  onExit,
  slideStyles,
}: AddStopsSlidingMenuProps) => {
  const { routeDispatch } = useRouteContext();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>(``);

  return (
    <>
      <SlidingMenu
        id="add-stops-sliding-menu"
        direction="down"
        background={RoutalPalette.white}
        isOpen={open}
        headerContent={
          <Grid item container direction="row" wrap="nowrap" alignItems="center" height="70px">
            <Text variant="h6" isBold intlId="driver.stops.create_stops.add_stops.title" />
          </Grid>
        }
        onClose={() => {
          if (addedStops.length > 0) setShowModal(true);
          else onExit();
        }}
        slideStyles={slideStyles}
        timeout={{ enter: 0 }}
        footerContent={
          <Grid
            item
            container
            direction="row"
            wrap="nowrap"
            style={{
              justifyContent: `center`,
              alignContent: `center`,
              height: `70px`,
              alignItems: `center`,
              borderTop: `1px solid ${RoutalPalette.white}`,
              width: `100%`,
              padding: `0 14px`,
              background: RoutalPalette.white,
            }}
          >
            <LoadingButton
              style={{
                height: `42px`,
                display: `flex`,
                flexDirection: `row`,
                flexWrap: `nowrap`,
                justifyContent: `center`,
                alignItems: `center`,
                borderRadius: `10px`,
                width: `100%`,
              }}
              onClick={(e: any) => {
                onClose();
              }}
              intlId="driver.stops.create_stops.next"
            />
          </Grid>
        }
      >
        <Grid
          container
          direction="column"
          style={{
            display: `flex`,
            flexWrap: `nowrap`,
            flexDirection: `column`,
            justifyContent: `space-between`,
            height: `100%`,
          }}
        >
          <Grid container direction="column" wrap="nowrap" style={{ overflow: `auto`, height: `100%` }}>
            <Grid item container direction="column" wrap="nowrap">
              <SearchInput
                onTextChange={(text) => setSearchText(text)}
                searchLive
                initialValue={searchText}
                voiceInput
                disableMicrophoneIfText
                source={`Stop creation`}
                inputContainerStyles={{
                  height: `55px`,
                  padding: `0 15px`,
                  borderRadius: `25px`,
                  border: `1px solid rgba(0, 0, 0, 0.2)`,
                }}
                clearButton
                placeholder={intl.formatMessage({
                  id: `driver.stop_list.input_search_ph_create`,
                })}
              />

              <VSpacer small />
              <Grid item container direction="column" wrap="nowrap" style={{ flex: 1 }}>
                {addedStops.map((stop, idx) => {
                  const location = stop.location;
                  if (!location) return null;
                  return (
                    <LocationPill
                      icon="remove"
                      key={`${location.location_id}-${idx}`}
                      location={location}
                      onClickOverIcon={async () => {
                        onRemoveStop(stop);
                      }}
                      onSelectLocation={() => {
                        routeDispatch({ type: RouteActionType.SET_EDITING_STOP, payload: stop });
                      }}
                    />
                  );
                })}

                {searchText !== `` && (
                  <>
                    <DividerWithText text={intl.formatMessage({ id: `driver.add_stops` })} />
                    <AddStopsList
                      icon="add"
                      searchText={searchText}
                      onSelectLocation={(location: Location) => {
                        onAddLocation(location);
                        setSearchText(``);
                      }}
                      refreshSession={searchText !== ``}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SlidingMenu>
      <Modal
        open={showModal}
        title={{
          intlId: `drivers.stop.stop_create.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.stop_create.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.stop_create.modal.leave`,
            onClick: () => {
              onExit();
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowModal(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />
    </>
  );
};
